//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoMap } from 'pojo-maps';
import { createSelector } from 'reselect';

import { PostDetailsPublishingStrategyMessage } from '@instagram-account/post-details/postDetailsUIConstants';
import {
  isValidIgReelsAspectRatio,
  isValidIgReelsDuration,
} from '@instagram-account/redux/instagram-reels-post-details/instagramReelsPostDetailsUtils';
import {
  getCurrentSelectedCuids,
  getCurrentSelectedReelsCuid,
} from '@instagram-account/redux/post-details/selectors/getCurrentSelectedCuids';
import { getIsEditingPost } from '@instagram-account/redux/post-details/selectors/getIsEditingPost';
import { getHasFbIgAutoPostingFeature } from '@instagram-account/redux/selectors/getHasFbIgAutoPostingFeature';
import { ICuralateStore } from '@models/AppState';
import Cuid from '@models/Cuid';
import { getMediaMetadata } from '@models/IMlsMediaItem';
import { isVideo as isMediaItemVideo } from '@models/INetworkItem';
import { InstagramPostType } from '@models/InstagramPostType';
import { InstagramPublishingStrategy } from '@models/InstagramPublishingStrategy';
import { getScheduleMediaMap } from '@redux/schedule-media/getScheduleMediaMap';
import { getMediaAspectRatio } from '@universal-scheduling/components/scheduler/post-details-tabs/postDetailsTabUtils';
import { isDefined } from '@utils/filterPredicates';

function getRelevantPostDetails(state: ICuralateStore) {
  const postType = state.instagram.postDetails.postType;

  const selectedCuids = (() => {
    if (postType === InstagramPostType.Reels) {
      const selectedReelsCuid = getCurrentSelectedReelsCuid(state);
      return isDefined(selectedReelsCuid) ? [selectedReelsCuid] : [];
    } else {
      return getCurrentSelectedCuids(state);
    }
  })();
  const scheduleMedia = getScheduleMediaMap(state);

  const selectedItems = selectedCuids.map((cuid) => {
    const mediaItem = PojoMap.get(scheduleMedia, cuid);
    const networkItem = mediaItem?.networkItem;

    const isVideo = !!networkItem && isMediaItemVideo(networkItem);
    const videoDuration = networkItem?.mediaMetadata?.durationMillis;

    const mediaMetadata = mediaItem ? getMediaMetadata(mediaItem) : undefined;
    const aspectRatio = getMediaAspectRatio(
      mediaMetadata?.width,
      mediaMetadata?.height,
    );

    return {
      cuid,
      isVideo,
      videoDuration,
      aspectRatio,
    };
  });

  return {
    postType: state.instagram.postDetails.postType,
    publishingStrategy:
      state.instagram.postDetails.currentPostDetails.publishingStrategy,
    selectedItems,
  };
}

interface IPublishingStrategyAndInfoText {
  validatedPublishingStrategy: InstagramPublishingStrategy;
  publishingStrategyDisabledMessage?: PostDetailsPublishingStrategyMessage;
}

// Shorthand for inline returns
function createInfoObject(
  strategy: InstagramPublishingStrategy,
  disabledMessage?: PostDetailsPublishingStrategyMessage,
): IPublishingStrategyAndInfoText {
  return {
    validatedPublishingStrategy: strategy,
    publishingStrategyDisabledMessage: disabledMessage,
  };
}

/*
 * We compute publishing strategy to allow us to lock the user
 * in manual when they are in situations that call for it.
 *
 * If they are locked into a particular publishing strategy, we will
 * also return info text from this selector to tell them why.
 *
 * NOTE: The order of the logic check below provides us with a consistent
 * hierarchy for these errors (story info text instead of auto info text,
 * etc). Please don't edit without consulting with PM/design.
 */
export const getPublishingStrategyAndInfoTextImpl = (
  isEditingPost: boolean,
  hasFbIgAutoPostingFeature: boolean,
  postDetails: {
    postType: InstagramPostType;
    publishingStrategy?: InstagramPublishingStrategy;
    selectedItems: {
      cuid: Cuid;
      isVideo: boolean;
      videoDuration?: number;
      aspectRatio: number;
    }[];
  },
): IPublishingStrategyAndInfoText => {
  const { postType, publishingStrategy, selectedItems } = postDetails;

  if (postType === InstagramPostType.Reels) {
    for (const item of selectedItems) {
      const { aspectRatio, videoDuration } = item;

      if (!isValidIgReelsAspectRatio(aspectRatio)) {
        return createInfoObject(
          InstagramPublishingStrategy.Manual,
          PostDetailsPublishingStrategyMessage.ReelsAspectRatio,
        );
      }

      if (isDefined(videoDuration) && !isValidIgReelsDuration(videoDuration)) {
        return createInfoObject(
          InstagramPublishingStrategy.Manual,
          PostDetailsPublishingStrategyMessage.ReelsVideoLength,
        );
      }
    }
  }

  // Only change the publishing strategy for them based on IG biz account
  // status if they are creating a new post. We'd rather let the update
  // fail than alter the existing post data.
  if (!isEditingPost) {
    if (!hasFbIgAutoPostingFeature) {
      return createInfoObject(
        InstagramPublishingStrategy.Manual,
        PostDetailsPublishingStrategyMessage.InstagramBusiness,
      );
    }
  }

  return createInfoObject(
    publishingStrategy ?? InstagramPublishingStrategy.Automatic,
  );
};

export const getPublishingStrategyAndInfoText = createSelector(
  getIsEditingPost,
  getHasFbIgAutoPostingFeature,
  getRelevantPostDetails,
  getPublishingStrategyAndInfoTextImpl,
);
